import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Archive, Building2, Play, Timer, Shuffle } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import HelpTip from "@/components/HelpTip";
import {
  autoArchiveNow,
  getClientBufferFlag,
  getClientAutoAssignFlag,
  listClients,
  updateClientSettings,
} from "@/lib/api";

export default function ClientSettings() {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [busy, setBusy] = useState(false);
  const [archiveDays, setArchiveDays] = useState("");
  const [bufferEnabled, setBufferEnabled] = useState(false);
  const [bufferSupported, setBufferSupported] = useState(false);
  const [autoAssign, setAutoAssign] = useState({ enabled: false, supported: false });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await listClients();
      if (cancelled) return;
      const list = data || [];
      setClients(list);
      if (list.length) {  // mount-time default (clientId is always empty here)
        setClientId(list[0].id);
        setArchiveDays(list[0].auto_archive_after_days ?? "");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Load buffer flag separately so pre-migration DBs don't break the page.
  useEffect(() => {
    if (!clientId) return undefined;
    let cancelled = false;
    (async () => {
      const flag = await getClientBufferFlag(clientId);
      if (cancelled) return;
      setBufferEnabled(flag.enabled);
      setBufferSupported(flag.supported);
      const aa = await getClientAutoAssignFlag(clientId);
      if (!cancelled) setAutoAssign(aa);
    })();
    return () => { cancelled = true; };
  }, [clientId]);

  const pickClient = (id) => {
    setClientId(id);
    const c = clients.find((x) => x.id === id);
    setArchiveDays(c?.auto_archive_after_days ?? "");
  };

  const saveArchive = async () => {
    if (!clientId) return;
    setBusy(true);
    const value = archiveDays === "" ? null : parseInt(archiveDays, 10);
    const { data, error } = await updateClientSettings(clientId, {
      auto_archive_after_days: Number.isNaN(value) ? null : value,
    });
    setBusy(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Auto-archive setting saved.");
    setClients((cs) => cs.map((c) => (c.id === clientId ? { ...c, ...data } : c)));
  };

  const runNow = async () => {
    setBusy(true);
    const { data, error } = await autoArchiveNow();
    setBusy(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(
      data?.moved > 0
        ? `${data.moved} booking${data.moved === 1 ? "" : "s"} archived.`
        : "Nothing to archive."
    );
  };

  const toggleBuffer = async (next) => {
    if (!clientId) return;
    setBusy(true);
    const prev = bufferEnabled;
    setBufferEnabled(next);
    const { data, error } = await updateClientSettings(clientId, {
      buffer_time_enabled: next,
    });
    setBusy(false);
    if (error) {
      setBufferEnabled(prev);
      toast.error(error.message);
      return;
    }
    toast.success(
      next
        ? "Turnaround buffer enabled for this client."
        : "Turnaround buffer disabled."
    );
    setClients((cs) => cs.map((c) => (c.id === clientId ? { ...c, ...data } : c)));
  };

  const toggleAutoAssign = async (next) => {
    setBusy(true);
    setAutoAssign((a) => ({ ...a, enabled: next }));
    const { error } = await updateClientSettings(clientId, { bay_auto_assign: next });
    setBusy(false);
    if (error) {
      setAutoAssign((a) => ({ ...a, enabled: !next }));
      toast.error(error.message);
      return;
    }
    toast.success(next ? "Bay auto-assign enabled." : "Bay auto-assign disabled.");
  };

  return (
    <div data-testid="client-settings-page" className="space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Building2 className="h-6 w-6" /> Client settings
          </h1>
          <p className="text-sm text-slate-500">
            Per-tenant policies. Currently exposes auto-archive — more knobs land
            in future phases.
          </p>
        </div>
        <Select value={clientId} onValueChange={pickClient}>
          <SelectTrigger className="w-72" data-testid="client-picker">
            <SelectValue placeholder="Pick a client" />
          </SelectTrigger>
          <SelectContent>
            {clients.map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Archive className="h-4 w-4" /> Auto-archive
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-slate-500">
            Completed, cancelled, or rejected bookings older than this many days
            are moved into <code className="font-mono">lbs_bookings_archive</code>.
            Leave blank to disable. A nightly Postgres cron job (defined in
            migration 015) runs <code className="font-mono">lbs_auto_archive_bookings()</code> at 02:15 UTC.
          </p>
          <div className="flex items-end gap-3 max-w-md">
            <div className="space-y-1.5 flex-1">
              <Label className="text-xs uppercase tracking-wider text-slate-500">
                Archive after (days)
              </Label>
              <Input
                type="number"
                min={1}
                value={archiveDays}
                onChange={(e) => setArchiveDays(e.target.value)}
                placeholder="e.g. 90 — blank = disabled"
                data-testid="archive-days"
              />
            </div>
            <Button onClick={saveArchive} disabled={busy} data-testid="archive-save">
              {busy ? "Saving…" : "Save"}
            </Button>
          </div>
          <div className="pt-2 border-t">
            <Button
              variant="outline"
              onClick={runNow}
              disabled={busy}
              data-testid="archive-run-now"
            >
              <Play className="h-4 w-4 mr-1.5" /> Run archive now (all clients)
            </Button>
            <p className="text-[11px] text-slate-500 mt-1.5">
              Service-role only. Useful for an on-demand sweep before a backup.
            </p>
          </div>
        </CardContent>
      </Card>

      {bufferSupported && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Timer className="h-4 w-4" /> Turnaround buffer
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-start justify-between gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-1.5">
                  <Label className="text-sm" data-testid="buffer-label">
                    Enable turnaround buffer between bookings
                  </Label>
                  <HelpTip id="client-buffer-switch">
                    When on, each booking leaves a configurable gap after it before
                    the bay can be reserved again. Default per site, override per bay.
                    When off, the feature is invisible on Site and Bay forms and the
                    scheduler behaves as it did before.
                  </HelpTip>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Set per-site default under <strong>Site manager → Edit site → Turnaround buffer</strong>,
                  and per-bay override under <strong>Edit bay → Turnaround buffer</strong>.
                </p>
              </div>
              <Switch
                checked={bufferEnabled}
                onCheckedChange={toggleBuffer}
                disabled={busy}
                data-testid="client-buffer-switch"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {autoAssign.supported && (
        <Card data-testid="auto-assign-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Shuffle className="h-4 w-4" /> Bay auto-assign
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-start justify-between gap-6">
              <div className="flex-1">
                <Label className="text-sm">Auto-assign the first free bay</Label>
                <p className="text-xs text-slate-500 mt-1">
                  On: public links and &ldquo;Any free bay&rdquo; pick a bay automatically. Off: carriers choose a bay on
                  public links and staff pick the bay themselves. Override per site under
                  <strong> Site manager → Edit site → Bay auto-assign</strong>.
                </p>
              </div>
              <Switch checked={autoAssign.enabled} onCheckedChange={toggleAutoAssign} disabled={busy} data-testid="client-auto-assign-switch" />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
